import React, { useEffect, useRef } from "react";
import { FileCode, FileText, Undo2, Send, Check, X } from "lucide-react";
import { Tooltip } from "@mui/material";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  SepaExportBatchRow,
  SepaDirectionEnum,
  ExportBatchStatusEnum,
} from "../../../../../stores/Sepa/sepa.types";
import { useKeyboardListSelection } from "../../../../../hooks/useKeyboardListSelection";
import { SepaEmptyBlock, SepaErrorBlock, SepaLoadingBlock } from "./SepaListStates";
import { getSepaErrorMessage } from "./sepaError";

const STATUS_LABEL: Record<string, string> = {
  [ExportBatchStatusEnum.Pending]: "Ausstehend",
  [ExportBatchStatusEnum.Completed]: "Erstellt",
  [ExportBatchStatusEnum.Failed]: "Fehlgeschlagen",
  [ExportBatchStatusEnum.SentToBank]: "An Bank gesendet",
  [ExportBatchStatusEnum.Accepted]: "Bestätigt",
  [ExportBatchStatusEnum.Rejected]: "Abgelehnt",
  [ExportBatchStatusEnum.Undone]: "Rückgängig",
  [ExportBatchStatusEnum.Reversed]: "Storniert",
};

const STATUS_CLASS: Record<string, string> = {
  [ExportBatchStatusEnum.Pending]: "border-amber-300 bg-amber-50 text-amber-700",
  [ExportBatchStatusEnum.Completed]: "border-sky-300 bg-sky-50 text-sky-700",
  [ExportBatchStatusEnum.Failed]: "border-rose-300 bg-rose-50 text-rose-700",
  [ExportBatchStatusEnum.SentToBank]: "border-indigo-300 bg-indigo-50 text-indigo-700",
  [ExportBatchStatusEnum.Accepted]: "border-emerald-300 bg-emerald-50 text-emerald-700",
  [ExportBatchStatusEnum.Rejected]: "border-rose-300 bg-rose-50 text-rose-700",
  [ExportBatchStatusEnum.Undone]: "border-slate-300 bg-slate-50 text-slate-500",
  [ExportBatchStatusEnum.Reversed]: "border-slate-300 bg-slate-50 text-slate-500",
};

/** Shared styling for the small circular action buttons in the last column. */
const ACTION_BTN =
  "h-7 w-7 border-0 text-white shadow-none transition-all duration-200 hover:scale-110 focus-visible:ring-0 focus-visible:ring-offset-0 active:scale-95 disabled:pointer-events-none disabled:opacity-40 [&_svg]:size-5";

const DIRECTION_LABEL: Record<string, string> = {
  [SepaDirectionEnum.DirectDebit]: "Lastschrift",
  [SepaDirectionEnum.CreditTransfer]: "Rückerstattung",
};

const fmtEur = (value: number): string =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value);

const fmtDate = (value?: string | Date | null): string =>
  value ? new Date(value).toLocaleDateString("de-DE") : "—";

interface Props {
  rows: SepaExportBatchRow[];
  onDownloadDocument: (documentId: string) => void;
  onUndo: (batch: SepaExportBatchRow) => void;
  onMarkSent: (batch: SepaExportBatchRow) => void;
  onMarkAccepted: (batch: SepaExportBatchRow) => void;
  onMarkRejected: (batch: SepaExportBatchRow) => void;
  /** Called when a row body is clicked (opens the export timeline). */
  onRowClick?: (batch: SepaExportBatchRow) => void;
  /** When set, the matching batch row is scrolled into view and briefly highlighted. */
  highlightBatchId?: string | null;
  /** Bumped on every navigate request so re-selecting the same batch re-flashes it. */
  highlightNonce?: number;
  busy?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  /** The query error (backend message is surfaced in the error state). */
  error?: unknown;
  /** Drop the own border/rounding so a parent scroll container owns the frame. */
  bare?: boolean;
}

/**
 * The "Exporte" table (one row per SEPA export batch). Each completed batch can be
 * downloaded (XML + summary PDF) or undone (returns its transactions to PENDING).
 */
const SepaExportBatchTable: React.FC<Props> = ({
  rows,
  onDownloadDocument,
  onUndo,
  onMarkSent,
  onMarkAccepted,
  onMarkRejected,
  onRowClick,
  highlightBatchId,
  highlightNonce,
  busy,
  isLoading,
  isError,
  error,
  bare,
}) => {
  const colSpan = 9;
  const thClass = "text-[13px] font-bold uppercase tracking-wide text-slate-600";

  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const handledNonce = useRef<number | undefined>(undefined);

  const { selectedId, setSelectedId, handleKeyDown } = useKeyboardListSelection({
    items: rows,
    getId: (row) => row.id,
    onActivate: onRowClick,
  });

  // Drive arrow/Enter navigation from a document listener rather than element focus:
  // the timeline dialog steals focus and never reliably returns it, which killed key
  // nav after closing it. Ignore keys while an input or any dialog is focused/open.
  useEffect(() => {
    if (!onRowClick) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const active = document.activeElement as HTMLElement | null;
      const tag = active?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || active?.isContentEditable) {
        return;
      }
      if (
        document.querySelector(
          '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"]',
        )
      ) {
        return;
      }
      handleKeyDown(event as unknown as React.KeyboardEvent);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [handleKeyDown, onRowClick]);

  // Navigating from a transaction's export chip: select that batch and centre it.
  // Guarded by the nonce so "Mehr laden" (rows change) never re-selects.
  useEffect(() => {
    if (!highlightBatchId || highlightNonce === handledNonce.current) return;
    const el = rowRefs.current.get(highlightBatchId);
    if (!el) return;
    setSelectedId(highlightBatchId);
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    handledNonce.current = highlightNonce;
  }, [highlightBatchId, highlightNonce, rows, setSelectedId]);

  // Keep the keyboard-selected row visible as it moves.
  useEffect(() => {
    if (selectedId) rowRefs.current.get(selectedId)?.scrollIntoView({ block: "nearest" });
  }, [selectedId]);

  return (
    <div className={bare ? "" : "overflow-hidden rounded-lg border border-solid border-slate-200"}>
      <Table>
        <TableHeader className="sticky top-0 z-10">
          <TableRow className="border-b border-solid border-slate-200 bg-slate-100 hover:bg-slate-100">
            <TableHead className={thClass}>Export Nr.</TableHead>
            <TableHead className={thClass}>Richtung</TableHead>
            <TableHead className={thClass}>Datum</TableHead>
            <TableHead className={`${thClass} text-center`}>Anzahl</TableHead>
            <TableHead className={`${thClass} text-right`}>Summe</TableHead>
            <TableHead className={`${thClass} text-center`}>Erstellt von</TableHead>
            <TableHead className={`${thClass} text-center`}>Status</TableHead>
            <TableHead className={`${thClass} text-center`}>Dokumente</TableHead>
            <TableHead className={`${thClass} text-center`}>Aktion</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={colSpan} className="p-0">
                <SepaLoadingBlock message="Exporte werden geladen…" />
              </TableCell>
            </TableRow>
          ) : isError ? (
            <TableRow>
              <TableCell colSpan={colSpan} className="p-0">
                <SepaErrorBlock
                  message={getSepaErrorMessage(error, "Exporte konnten nicht geladen werden.")}
                />
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colSpan} className="p-0">
                <SepaEmptyBlock message="Noch keine Exporte für diesen Mandanten." />
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, index) => {
              const zebra = index % 2 === 1 ? "bg-slate-100" : "bg-white";
              const createdBy = row.createdBy
                ? `${row.createdBy.firstName ?? ""} ${row.createdBy.lastName ?? ""}`.trim()
                : "";
              const canUndo = row.status === ExportBatchStatusEnum.Completed;
              const isSent = row.status === ExportBatchStatusEnum.SentToBank;
              return (
                <TableRow
                  key={row.id}
                  ref={(el) => {
                    if (el) rowRefs.current.set(row.id, el);
                    else rowRefs.current.delete(row.id);
                  }}
                  onClick={
                    onRowClick
                      ? () => {
                          setSelectedId(row.id);
                          onRowClick(row);
                        }
                      : undefined
                  }
                  className={`text-left text-[15px] ${onRowClick ? "cursor-pointer" : ""} ${
                    selectedId === row.id
                      ? "bg-sky-100 ring-1 ring-inset ring-sky-300 hover:bg-sky-100"
                      : `${zebra} hover:bg-slate-100/70`
                  }`}
                >
                  <TableCell className="py-1 font-medium text-slate-800">
                    {row.batchNumber}
                  </TableCell>
                  <TableCell className="py-1 text-slate-700">
                    {DIRECTION_LABEL[row.direction] ?? row.direction}
                  </TableCell>
                  <TableCell className="whitespace-nowrap py-1 text-slate-600">
                    {fmtDate(row.createdAt)}
                  </TableCell>
                  <TableCell className="py-1 text-center text-slate-700">
                    {row.transactionCount}
                  </TableCell>
                  <TableCell className="whitespace-nowrap py-1 text-right font-semibold text-slate-800">
                    {fmtEur(Number(row.totalAmount))}
                  </TableCell>
                  <TableCell className="py-1 text-center text-slate-600">
                    {createdBy || "—"}
                  </TableCell>
                  <TableCell className="py-1 text-center">
                    <Badge
                      variant="outline"
                      className={`text-sm font-semibold ${STATUS_CLASS[row.status] ?? ""}`}
                    >
                      {STATUS_LABEL[row.status] ?? row.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-1" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-2">
                      <Tooltip title="SEPA-XML herunterladen">
                        <span className="inline-flex">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 border-0 bg-transparent text-[#367b9a] shadow-none hover:bg-transparent hover:text-[#367b9a] focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-30 [&_svg]:size-6"
                            onClick={() =>
                              row.xmlDocument?.id && onDownloadDocument(row.xmlDocument.id)
                            }
                            disabled={!row.xmlDocument?.id}
                            aria-label="XML herunterladen"
                          >
                            <FileCode />
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Zusammenfassung (PDF) herunterladen">
                        <span className="inline-flex">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 border-0 bg-transparent text-[#367b9a] shadow-none hover:bg-transparent hover:text-[#367b9a] focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-30 [&_svg]:size-6"
                            onClick={() =>
                              row.pdfDocument?.id && onDownloadDocument(row.pdfDocument.id)
                            }
                            disabled={!row.pdfDocument?.id}
                            aria-label="PDF herunterladen"
                          >
                            <FileText />
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                  </TableCell>
                  <TableCell className="py-1" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1.5">
                      {canUndo && (
                        <>
                          <Tooltip title="Als an die Bank gesendet markieren">
                            <span className="inline-flex">
                              <Button
                                size="icon"
                                className={`${ACTION_BTN} bg-[#223b63] hover:bg-[#24546b]`}
                                onClick={() => onMarkSent(row)}
                                disabled={busy}
                                aria-label="Als gesendet markieren"
                              >
                                <Send />
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Export rückgängig machen">
                            <span className="inline-flex">
                              <Button
                                size="icon"
                                className={`${ACTION_BTN} bg-orange-500 hover:bg-orange-600`}
                                onClick={() => onUndo(row)}
                                disabled={busy}
                                aria-label="Rückgängig"
                              >
                                <Undo2 />
                              </Button>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      {isSent && (
                        <>
                          <Tooltip title="Bankbestätigung erfassen">
                            <span className="inline-flex">
                              <Button
                                size="icon"
                                className={`${ACTION_BTN} bg-emerald-600 hover:bg-emerald-700`}
                                onClick={() => onMarkAccepted(row)}
                                disabled={busy}
                                aria-label="Bestätigen"
                              >
                                <Check />
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Bankablehnung erfassen">
                            <span className="inline-flex">
                              <Button
                                size="icon"
                                className={`${ACTION_BTN} bg-rose-600 hover:bg-rose-700`}
                                onClick={() => onMarkRejected(row)}
                                disabled={busy}
                                aria-label="Ablehnen"
                              >
                                <X />
                              </Button>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      {!canUndo && !isSent && <span className="text-slate-300">—</span>}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default SepaExportBatchTable;
